import React, { useState, useEffect, useCallback } from 'react';
import { shippingPriceApi } from '../services/api';
import { ShippingPriceHistory } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const PriceHistoryPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [history, setHistory] = useState<ShippingPriceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredHistory, setFilteredHistory] = useState<ShippingPriceHistory[]>([]);
  const [filters, setFilters] = useState({
    location: '',
    locationType: '',
    action: '',
    dateFrom: '',
    dateTo: '',
    changedBy: ''
  });

  const fetchHistory = async () => {
    try {
      const response = await shippingPriceApi.getHistory();
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching price history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterHistory = useCallback(() => {
    let filtered = [...history];

    if (filters.location) {
      filtered = filtered.filter(h => 
        h.locationName.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    if (filters.locationType) {
      filtered = filtered.filter(h => h.locationType === filters.locationType);
    }

    if (filters.action) {
      filtered = filtered.filter(h => h.action === filters.action);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(h => 
        new Date(h.changeDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(h => 
        new Date(h.changeDate) <= new Date(filters.dateTo + 'T23:59:59')
      );
    }

    if (filters.changedBy) {
      filtered = filtered.filter(h => 
        h.changedByUserName.toLowerCase().includes(filters.changedBy.toLowerCase())
      );
    }

    setFilteredHistory(filtered);
  }, [history, filters]);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    filterHistory();
  }, [filterHistory]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      locationType: '',
      action: '',
      dateFrom: '',
      dateTo: '',
      changedBy: ''
    });
  };

  const getActionBadgeClass = (action: string) => {
    switch (action) {
      case 'Created':
        return 'bg-success';
      case 'Updated':
        return 'bg-primary';
      case 'Deleted':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const formatPrice = (price?: number) => {
    return price !== undefined && price !== null ? `${price.toLocaleString('vi-VN')} ₫` : t('no.data');
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>{t('shipping.prices.history')}</h2>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-outline-primary btn-sm me-3"
                              onClick={() => navigate('/pricing/fee-structure')}
              >
                {t('back.to.shipping.prices')}
              </button>
              <span className="me-3 text-muted">
                {t('showing.results').replace('{start}', filteredHistory.length.toString()).replace('{end}', filteredHistory.length.toString()).replace('{total}', history.length.toString())}
              </span>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={clearFilters}
              >
                {t('clear') + ' ' + t('filters')}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">{t('filters')}</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label htmlFor="locationFilter" className="form-label">{t('location')}</label>
                  <input
                    type="text"
                    className="form-control"
                    id="locationFilter"
                    placeholder={t('search') + ' ' + t('location') + '...'}
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="locationTypeFilter" className="form-label">{t('type')}</label>
                  <select
                    className="form-select"
                    id="locationTypeFilter"
                    value={filters.locationType}
                    onChange={(e) => handleFilterChange('locationType', e.target.value)}
                  >
                    <option value="">{t('all.types') || 'All Types'}</option>
                    <option value="Province">{t('province')}</option>
                    <option value="District">{t('district')}</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label htmlFor="actionFilter" className="form-label">{t('action')}</label>
                  <select
                    className="form-select"
                    id="actionFilter"
                    value={filters.action}
                    onChange={(e) => handleFilterChange('action', e.target.value)}
                  >
                    <option value="">{t('all.actions') || 'All Actions'}</option>
                    <option value="Created">{t('created')}</option>
                    <option value="Updated">{t('updated')}</option>
                    <option value="Deleted">{t('deleted')}</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label htmlFor="dateFromFilter" className="form-label">{t('from.date') || 'From Date'}</label>
                  <input
                    type="date"
                    className="form-control"
                    id="dateFromFilter"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="dateToFilter" className="form-label">{t('to.date') || 'To Date'}</label>
                  <input
                    type="date"
                    className="form-control"
                    id="dateToFilter"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </div>
                <div className="col-md-1">
                  <label htmlFor="changedByFilter" className="form-label">{t('changed.by') || 'Changed By'}</label>
                  <input
                    type="text"
                    className="form-control"
                    id="changedByFilter"
                    placeholder={t('user') + '...'}
                    value={filters.changedBy}
                    onChange={(e) => handleFilterChange('changedBy', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* History Table */}
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t('date') + ' & ' + t('time') || 'Date & Time'}</th>
                      <th>{t('affected.date') || 'Affected Date'}</th> {/* New column */}
                      <th>{t('location')}</th>
                      <th>{t('type')}</th>
                      <th>{t('action')}</th>
                      <th>{t('old.price') || 'Old Price'}</th>
                      <th>{t('new.price') || 'New Price'}</th>
                      <th>{t('price.change') || 'Price Change'}</th>
                      <th>{t('reason')}</th>
                      <th>{t('changed.by') || 'Changed By'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center text-muted py-4">
                          {history.length === 0 ? t('no.price.history.available') : t('no.records.match.filters')}
                        </td>
                      </tr>
                    ) : (
                      filteredHistory.map((record) => {
                        const priceDiff = record.oldPrice !== undefined && record.oldPrice !== null
                          ? record.newPrice - record.oldPrice
                          : 0;
                        return (
                          <tr key={record.id}>
                            <td>{formatDateTime(record.changeDate)}</td>
                            <td>{record.affectedDate ? new Date(record.affectedDate).toLocaleDateString() : '-'}</td>
                            <td>{record.locationName}</td>
                            <td>
                              <span className={`badge ${record.locationType === 'Province' ? 'bg-success' : 'bg-info'}`}>
                                {t(record.locationType.toLowerCase())}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${getActionBadgeClass(record.action)}`}>
                                {t(record.action.toLowerCase())}
                              </span>
                            </td>
                            <td>{formatPrice(record.oldPrice)}</td>
                            <td>{formatPrice(record.newPrice)}</td>
                            <td>
                              {record.action !== 'Created' && record.action !== 'Deleted' && priceDiff !== 0 && (
                                <span className={`badge ${priceDiff > 0 ? 'bg-warning' : 'bg-info'}`}>
                                  {priceDiff > 0 ? '+' : ''}{formatPrice(priceDiff).replace(' ₫', '')}
                                </span>
                              )}
                            </td>
                            <td>
                              {record.reason ? (
                                <span title={record.reason} className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                                  {record.reason}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{record.changedByUserName}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          {filteredHistory.length > 0 && (
            <div className="row mt-4">
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5 className="card-title text-success">
                      {filteredHistory.filter(h => h.action === 'Created').length}
                    </h5>
                    <p className="card-text">{t('created')}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5 className="card-title text-primary">
                      {filteredHistory.filter(h => h.action === 'Updated').length}
                    </h5>
                    <p className="card-text">{t('updated')}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5 className="card-title text-danger">
                      {filteredHistory.filter(h => h.action === 'Deleted').length}
                    </h5>
                    <p className="card-text">{t('deleted')}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5 className="card-title text-info">
                      {new Set(filteredHistory.map(h => h.changedByUserName)).size}
                    </h5>
                    <p className="card-text">{t('users.involved') || 'Users Involved'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryPage;